/**
 * Club Order History - Unified view of Reservations + POS income
 * Shows all club revenue sources: POS sales, reservations, etc.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar, Store, DollarSign, Package,
  ChevronLeft, RefreshCw, Filter, Search, ExternalLink,
  CreditCard, Banknote, Truck, CheckCircle, Clock, XCircle,
  ChevronDown, ChevronUp, User, MapPin
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import ClubNavbar from '../../components/ClubNavbar';
import api from '../../services/api';
import { toast } from 'sonner';

// Stat card component
const StatCard = ({ icon: Icon, label, value, subValue, iconColor = "text-primary", bgColor = "bg-primary/10" }) => (
  <Card className="bg-card">
    <CardContent className="p-4">
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl ${bgColor} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-sm text-muted-foreground">{label}</p>
          {subValue && <p className="text-xs text-muted-foreground mt-0.5">{subValue}</p>}
        </div>
      </div>
    </CardContent>
  </Card>
);

// Order row component
const OrderRow = ({ order, isExpanded, onToggle }) => {
  const isReservation = order.source === 'reservation';
  const isPOS = order.source === 'pos';
  const isEcommerce = order.source === 'ecommerce';
  
  const getStatusBadge = (status) => {
    const statusConfig = {
      'pendiente': { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Pendiente' },
      'pagado': { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Pagado' },
      'completed': { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Completado' },
      'procesando': { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Procesando' },
      'enviado': { bg: 'bg-purple-500/20', text: 'text-purple-400', label: 'Enviado' },
      'entregado': { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Entregado' },
      'cancelado': { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Cancelado' },
      'refunded': { bg: 'bg-orange-500/20', text: 'text-orange-400', label: 'Reembolsado' },
    };
    const config = statusConfig[status] || statusConfig['pendiente'];
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getPaymentIcon = (method) => {
    switch (method) {
      case 'cash':
        return <Banknote className="w-4 h-4 text-green-500" />;
      case 'card':
        return <CreditCard className="w-4 h-4 text-blue-500" />;
      default:
        return <CreditCard className="w-4 h-4 text-purple-500" />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <motion.div
      layout
      className="bg-card rounded-xl border border-border overflow-hidden"
    >
      {/* Order header */}
      <button
        onClick={onToggle}
        className="w-full p-4 flex items-center justify-between hover:bg-secondary/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          {/* Source badge */}
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            isReservation ? 'bg-blue-500/20' : 'bg-purple-500/20'
          }`}>
            {isReservation ? (
              <Calendar className="w-5 h-5 text-blue-500" />
            ) : (
              <Store className="w-5 h-5 text-purple-500" />
            )}
          </div>
          
          <div className="text-left">
            <div className="flex items-center gap-2">
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                isReservation ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'
              }`}>
                {order.source_label}
              </span>
              {getStatusBadge(order.status)}
            </div>
            <p className="font-medium mt-1">
              {isPOS ? order.sale_number || `#${order.id?.slice(-8)}` : `#${order.id?.slice(-8)}`}
            </p>
            <p className="text-xs text-muted-foreground">{formatDate(order.date)}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-lg font-bold text-primary">${order.total?.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">{order.items_count} artículo(s)</p>
          </div>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-muted-foreground" />
          ) : (
            <ChevronDown className="w-5 h-5 text-muted-foreground" />
          )}
        </div>
      </button>
      
      {/* Expanded details */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="border-t border-border"
          >
            <div className="p-4 space-y-4">
              {/* Customer info */}
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <p className="text-sm font-medium text-muted-foreground mb-1">Cliente</p>
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span>{order.customer_name}</span>
                  </div>
                  {order.customer_email && (
                    <p className="text-xs text-muted-foreground ml-6">{order.customer_email}</p>
                  )}
                </div>
                
                <div className="flex-1">
                  <p className="text-sm font-medium text-muted-foreground mb-1">Pago</p>
                  <div className="flex items-center gap-2">
                    {getPaymentIcon(order.payment_method)}
                    <span className="capitalize">{order.payment_method === 'cash' ? 'Efectivo' : order.payment_method === 'card' ? 'Tarjeta' : order.payment_method}</span>
                  </div>
                </div>
                
                {isEcommerce && order.shipping_city && (
                  <div className="flex-1">
                    <p className="text-sm font-medium text-muted-foreground mb-1">Envío</p>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-muted-foreground" />
                      <span>{order.shipping_city}</span>
                    </div>
                  </div>
                )}
                
                {!isEcommerce && order.operator_name && (
                  <div className="flex-1">
                    <p className="text-sm font-medium text-muted-foreground mb-1">Operador</p>
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-muted-foreground" />
                      <span>{order.operator_name}</span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Items */}
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Productos</p>
                <div className="space-y-2">
                  {order.items?.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-secondary/30 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">{item.name || item.product_name}</span>
                        <span className="text-xs text-muted-foreground">x{item.quantity}</span>
                      </div>
                      <span className="font-medium">${(item.price * item.quantity).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Totals */}
              <div className="border-t border-border pt-3 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>${order.subtotal?.toLocaleString()}</span>
                </div>
                {order.discount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Descuento</span>
                    <span className="text-red-400">-${order.discount?.toLocaleString()}</span>
                  </div>
                )}
                {order.shipping_cost > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Envío</span>
                    <span>${order.shipping_cost?.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg pt-2">
                  <span>Total</span>
                  <span className="text-primary">${order.total?.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default function ClubOrderHistory() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  
  // Filters
  const [period, setPeriod] = useState('month');
  const [source, setSource] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/club-owner/order-history', {
        params: { period, source }
      });
      setOrders(response.data.orders || []);
      setSummary(response.data.summary);
    } catch (err) {
      console.error('Error fetching order history:', err);
      toast.error('Error al cargar el historial de pedidos');
    } finally {
      setLoading(false);
    }
  }, [period, source]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Filter orders by search term
  const filteredOrders = orders.filter(order => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      order.customer_name?.toLowerCase().includes(search) ||
      order.customer_email?.toLowerCase().includes(search) ||
      order.id?.toLowerCase().includes(search) ||
      order.sale_number?.toLowerCase().includes(search)
    );
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <ClubNavbar />
      
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between p-4 max-w-6xl mx-auto">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/club')}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-display font-bold text-lg">Últimas Ventas</h1>
              <p className="text-xs text-muted-foreground">Ingresos del Club: Reservaciones + POS</p>
            </div>
          </div>
          
          <Button variant="outline" size="sm" onClick={fetchOrders} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-4 space-y-6">
        {/* Stats */}
        {summary && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-3"
          >
            <StatCard
              icon={DollarSign}
              label="Ingresos Totales"
              value={formatCurrency(summary.total_revenue)}
              iconColor="text-green-500"
              bgColor="bg-green-500/10"
            />
            <StatCard
              icon={Calendar}
              label="Reservaciones"
              value={summary.reservation_count || 0}
              subValue={formatCurrency(summary.revenue_reservations || 0)}
              iconColor="text-blue-500"
              bgColor="bg-blue-500/10"
            />
            <StatCard
              icon={Store}
              label="Punto de Venta"
              value={summary.pos_sales}
              subValue={formatCurrency(summary.revenue_pos)}
              iconColor="text-purple-500"
              bgColor="bg-purple-500/10"
            />
            <StatCard
              icon={Package}
              label="Total Transacciones"
              value={summary.total_orders}
              iconColor="text-orange-500"
              bgColor="bg-orange-500/10"
            />
          </motion.div>
        )}

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-col sm:flex-row gap-3"
        >
          {/* Period Filter */}
          <div className="flex items-center gap-1 bg-secondary/50 rounded-lg p-1">
            {[
              { value: 'day', label: 'Hoy' },
              { value: 'week', label: '7 días' },
              { value: 'month', label: '30 días' },
              { value: 'year', label: 'Año' },
              { value: 'all', label: 'Todo' },
            ].map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  period === p.value
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-secondary text-muted-foreground'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Source Filter - Income sources for the club */}
          <div className="flex items-center gap-1 bg-secondary/50 rounded-lg p-1">
            {[
              { value: 'all', label: 'Todos', icon: Filter },
              { value: 'reservation', label: 'Reservaciones', icon: Calendar },
              { value: 'pos', label: 'POS', icon: Store },
            ].map((s) => (
              <button
                key={s.value}
                onClick={() => setSource(s.value)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1 ${
                  source === s.value
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-secondary text-muted-foreground'
                }`}
              >
                <s.icon className="w-3 h-3" />
                {s.label}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por cliente, email o número..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        </motion.div>

        {/* Orders List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-3"
        >
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredOrders.length === 0 ? (
            <Card className="bg-card">
              <CardContent className="p-12 text-center">
                <Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-lg font-medium">No hay pedidos</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {searchTerm ? 'No se encontraron resultados para tu búsqueda' : 'Los pedidos aparecerán aquí'}
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredOrders.map((order) => (
              <OrderRow
                key={order.id}
                order={order}
                isExpanded={expandedId === order.id}
                onToggle={() => setExpandedId(expandedId === order.id ? null : order.id)}
              />
            ))
          )}
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex flex-wrap gap-3 pt-4"
        >
          <Button variant="outline" onClick={() => navigate('/club')}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Volver al Dashboard
          </Button>
          <Button variant="outline" onClick={() => navigate('/pos')}>
            <Store className="w-4 h-4 mr-2" />
            Ir al POS
          </Button>
        </motion.div>
      </div>
    </div>
  );
}
